import { useState } from 'react';
import { ClipboardCheck, ExternalLink } from 'lucide-react';
import {
  usePendingCompetencyEvidence,
  useReviewCompetencyEvidence,
  type PendingEvidence,
} from '@/api/competency-evidence';
import { formatDate } from '@/lib/formatters';

function ownerName(e: PendingEvidence) {
  return `${e.owner.first_name} ${e.owner.last_name}`;
}

// Approver view (supervisor or approval-group member). Self-hides when the user
// has nothing to review — the API only returns evidence they may act on.
export function EvidenceReviewSection() {
  const { data } = usePendingCompetencyEvidence();
  const reviewMut = useReviewCompetencyEvidence();
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  const items = data ?? [];
  if (items.length === 0) return null;

  const approve = (id: number) => reviewMut.mutate({ id, data: { status: 'approved' } });
  const reject = (id: number) => {
    if (!reason.trim()) return;
    reviewMut.mutate(
      { id, data: { status: 'rejected', review_notes: reason.trim() } },
      {
        onSuccess: () => {
          setRejectingId(null);
          setReason('');
        },
      },
    );
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Evidencias por revisar</h2>
      </div>
      <ul className="space-y-2">
        {items.map((e) => (
          <li key={e.id} className="rounded-xl border border-border bg-card p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-card-foreground">
                  {e.activity?.position_competency.name ?? 'Competencia'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {ownerName(e)} · cargado el {formatDate(e.created_at)}
                </p>
                <a
                  href={e.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> {e.file_name}
                </a>
              </div>
              {rejectingId !== e.id && (
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    onClick={() => setRejectingId(e.id)}
                    disabled={reviewMut.isPending}
                    className="cursor-pointer rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Rechazar
                  </button>
                  <button
                    onClick={() => approve(e.id)}
                    disabled={reviewMut.isPending}
                    className="cursor-pointer rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Aprobar
                  </button>
                </div>
              )}
            </div>

            {rejectingId === e.id && (
              <div className="mt-3 space-y-2">
                <textarea
                  value={reason}
                  onChange={(ev) => setReason(ev.target.value)}
                  rows={2}
                  placeholder="Explica el motivo del rechazo"
                  className="w-full rounded-lg border border-input bg-background px-2 py-1.5 text-sm text-foreground"
                />
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => {
                      setRejectingId(null);
                      setReason('');
                    }}
                    className="cursor-pointer rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => reject(e.id)}
                    disabled={!reason.trim() || reviewMut.isPending}
                    className="cursor-pointer rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Confirmar rechazo
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
